/**
 * Rich mock data for demo mode — one per role.
 * Shaped to match the raw API contract that mapPlayerDashboardData expects.
 */

export const mockPlayer = {
  id: 1,
  email: 'jaylen@demo.com',
  role: 'player',
  first_name: 'Jaylen',
  last_name: 'Carter',
  team: 'Northside Elite U16',
  avatar_url: null,
  readiness: 87,
  streak: 12,
  week_sessions: 4,
  shooting_accuracy: 72,
  accuracy_trend: 3.2,
  weekly_bars: [40, 55, 45, 60, 70, 50, 65, 75, 80, 60, 70, 85, 78, 82],
  sessions_completed: 47,
  consistency_score: 88,
  ghost_radar_level: 'rising',
  ghost_radar_skills: {
    shooting: { current: 82, ghost: 68 },
    handles: { current: 71, ghost: 65 },
    defense: { current: 60, ghost: 55 },
    iq: { current: 75, ghost: 58 },
    athleticism: { current: 88, ghost: 80 },
  },
  athlete_signals: {
    status: 'ready',
    sport_key: 'basketball',
    generated_at: '2026-09-07T18:00:00.000Z',
    evidence: {
      completed_session_count: 47,
      evidence_ready_session_count: 43,
      verified_session_count: 40,
      sessions_last_7_days: 4,
      sessions_last_28_days: 12,
      active_weeks_last_28_days: 4,
      evidence_rhythm_pct: 100,
      verified_evidence_pct: 93,
      latest_session: {
        id: 'demo-session-1',
        sport_key: 'basketball',
        sport_name: 'Basketball',
        session_type: 'assessment',
        status: 'completed',
        started_at: '2026-09-06T18:00:00.000Z',
        ended_at: '2026-09-06T18:00:12.000Z',
        duration_seconds: 12,
        verification_level: 2,
        confidence_score: 0.92,
        capture_quality: 'good_capture',
        evidence_ready: true,
        provenance_complete: true,
        measurement_count: 720,
        metric_count: 2,
        frame_count: 360,
        observed_frame_rate_hz: 30,
        device_count: 1,
        capabilities: ['accelerometer', 'gyroscope'],
      },
      history_8_weeks: [
        { week_index: 0, completed_sessions: 3, evidence_ready_sessions: 3, verified_sessions: 2 },
        { week_index: 1, completed_sessions: 4, evidence_ready_sessions: 4, verified_sessions: 4 },
        { week_index: 2, completed_sessions: 2, evidence_ready_sessions: 2, verified_sessions: 2 },
        { week_index: 3, completed_sessions: 3, evidence_ready_sessions: 2, verified_sessions: 2 },
        { week_index: 4, completed_sessions: 4, evidence_ready_sessions: 4, verified_sessions: 4 },
        { week_index: 5, completed_sessions: 2, evidence_ready_sessions: 2, verified_sessions: 2 },
        { week_index: 6, completed_sessions: 4, evidence_ready_sessions: 3, verified_sessions: 3 },
        { week_index: 7, completed_sessions: 4, evidence_ready_sessions: 4, verified_sessions: 4 },
      ],
    },
    baseline: {
      status: 'tracking',
      comparable_session_count: 6,
      excluded_session_count: 2,
      protocol: {
        standardized_test: true,
        session_type: 'assessment',
        capture_mode: 'mobile_phone_assessment_v1',
        provider_id: 'browser-motion',
        test_protocol_key: 'basketball_burst_brake',
        test_protocol_version: '1.0.0',
        device_placement: 'front_waistband_center',
      },
      metrics: [
        {
          key: 'acceleration_rms',
          label: 'Acceleration RMS',
          unit: 'm/s²',
          baseline: 4.1,
          previous: 4.4,
          current: 4.6,
          change_from_baseline_pct: 12.2,
          change_from_previous_pct: 4.5,
        },
      ],
    },
    derived: {
      result_count: 36,
      session_count: 6,
      metric_count: 6,
      metrics: [
        {
          metric_key: 'burst_acceleration_signal_peak',
          unit: 'm/s²',
          current: 12.4,
          previous: 11.8,
          change_from_previous_pct: 5.1,
          verification_level: 2,
          evidence_status: 'eligible',
          session_id: 'demo-session-1',
          session_started_at: '2026-09-06T18:00:00.000Z',
          derivation_method: 'basketball_burst_brake_phase_metrics',
          derivation_version: '1.0.0',
        },
        {
          metric_key: 'brake_acceleration_signal_peak',
          unit: 'm/s²',
          current: 10.8,
          previous: 10.2,
          change_from_previous_pct: 5.9,
          verification_level: 2,
          evidence_status: 'eligible',
          session_id: 'demo-session-1',
          session_started_at: '2026-09-06T18:00:00.000Z',
          derivation_method: 'basketball_burst_brake_phase_metrics',
          derivation_version: '1.0.0',
        },
      ],
    },
    calibration: {
      status: 'ready',
      ghost_radar_state: 'evidence_ready_calibration_pending',
      calibrated_result_count: 0,
      model_count: 2,
      evidence_ready_model_count: 2,
      score_ready_model_count: 0,
      models: [
        {
          calibration_key: 'basketball_burst_v1',
          output_attribute_key: 'burst_signal',
          status: 'active',
          minimum_evidence_sessions: 3,
          evidence_session_count: 6,
          evidence_ready: true,
          score_ready: false,
          validated: false,
          score_emission_enabled: false,
        },
        {
          calibration_key: 'basketball_brake_v1',
          output_attribute_key: 'brake_signal',
          status: 'active',
          minimum_evidence_sessions: 3,
          evidence_session_count: 6,
          evidence_ready: true,
          score_ready: false,
          validated: false,
          score_emission_enabled: false,
        },
      ],
    },
  },
  sensor_fabric: {
    enabled: true,
    device_count: 3,
    trusted_device_count: 2,
    capabilities: ['accelerometer', 'gyroscope', 'heart_rate', 'rr_interval', 'gps', 'wifi_csi'],
    verification_level: 3,
    confidence_score: 0.92,
    latest_session: {
      id: 'demo-session-1',
      status: 'completed',
      sport_key: 'basketball',
      session_type: 'training',
    },
    devices: [
      {
        id: 'demo-phone',
        type: 'phone',
        name: 'Player Phone',
        trusted: true,
        capabilities: ['accelerometer', 'gyroscope', 'gps'],
      },
      {
        id: 'demo-polar',
        type: 'chest_strap',
        name: 'Polar H10',
        trusted: true,
        capabilities: ['heart_rate', 'rr_interval', 'accelerometer'],
      },
      {
        id: 'demo-wifi',
        type: 'wifi_node',
        name: 'ARGUS Wi-Fi Node',
        trusted: false,
        capabilities: ['wifi_csi'],
      },
    ],
  },
  goals: [
    { id: 1, title: 'Hit 500 free throws', progress: 68 },
    { id: 2, title: 'Complete Ball Handling Level 3', progress: 45 },
    { id: 3, title: 'Finish 20 IQ Modules', progress: 80 },
  ],
  assignments: [
    { id: 1, name: 'Morning Shooting Drill', duration_minutes: 25, drill_count: 6 },
    { id: 2, name: 'Defensive Footwork', duration_minutes: 20, drill_count: 4 },
  ],
  badges: [
    { id: 1, name: 'Sharpshooter', icon: '🎯' },
    { id: 2, name: '7-Day Streak', icon: '🔥' },
    { id: 3, name: 'IQ Rising', icon: '🧠' },
    { id: 4, name: 'Iron Defense', icon: '🛡️' },
    { id: 5, name: 'First Session', icon: '⭐' },
  ],
  campaigns: [
    {
      id: 'demo-handle-20',
      name: 'Handle 20',
      owner: 'ProStep Athletics · Demo',
      type: 'challenge',
      sport: 'Basketball',
      age_band: 'U13–U15',
      progress: 12,
      target: 20,
      unit: 'sessions',
      badge: 'Handle 20 Badge',
    },
    {
      id: 'demo-community-shots',
      name: 'Copenhagen Million Shot Goal',
      owner: 'Hoop Dynamics Community · Demo',
      type: 'community_goal',
      sport: 'Basketball',
      age_band: 'All ages',
      progress: 782450,
      target: 1000000,
      unit: 'made shots',
      badge: 'Community Builder',
    },
  ],
  iq_assignments: [
    { id: 1, title: 'Pick & Roll Reads', question_count: 12, status: 'available' },
    { id: 2, title: 'Transition Defense', question_count: 8, status: 'completed' },
    { id: 3, title: 'Spacing & Movement', question_count: 10, status: 'available' },
  ],
}

export const mockCoach = {
  id: 2,
  email: 'coach@demo.com',
  role: 'coach',
  first_name: 'Marcus',
  last_name: 'Williams',
  team: 'Northside Elite',
  players: [
    { id: 1, name: 'Jaylen Carter', readiness: 87, streak: 12, lastActive: '2 hrs ago' },
    { id: 2, name: 'Devon Mitchell', readiness: 74, streak: 5, lastActive: '1 day ago' },
    { id: 3, name: 'Amir Johnson', readiness: 92, streak: 18, lastActive: '4 hrs ago' },
    { id: 4, name: 'Kai Thompson', readiness: 65, streak: 2, lastActive: '3 days ago' },
    { id: 5, name: 'Zion Brooks', readiness: 81, streak: 9, lastActive: '6 hrs ago' },
  ],
  programs: [
    {
      id: 1,
      name: 'Ball Handling Fundamentals',
      assignedCount: 4,
      drillCount: 12,
      status: 'active',
    },
    { id: 2, name: 'Shooting Progression', assignedCount: 5, drillCount: 8, status: 'active' },
    { id: 3, name: 'Defensive Rotations', assignedCount: 3, drillCount: 6, status: 'draft' },
  ],
  teamStats: {
    avgReadiness: 80,
    activePlayers: 5,
    totalPlayers: 8,
    completionRate: 73,
  },
}

export const mockClub = {
  id: 3,
  email: 'admin@northsideelite.com',
  role: 'club',
  first_name: 'Sarah',
  last_name: 'Chen',
  clubName: 'Northside Elite Basketball',
  teams: [
    { id: 1, name: 'U16 Boys', coachName: 'Marcus Williams', playerCount: 8, avgReadiness: 80 },
    { id: 2, name: 'U14 Boys', coachName: 'Lisa Park', playerCount: 12, avgReadiness: 71 },
    { id: 3, name: 'U16 Girls', coachName: 'James Rivera', playerCount: 10, avgReadiness: 85 },
  ],
  overview: {
    totalPlayers: 30,
    activeCoaches: 3,
    avgEngagement: 76,
    monthlyGrowth: 12,
  },
}

export const mockSponsor = {
  id: 4,
  email: 'brand@sportswear.com',
  role: 'sponsor',
  first_name: 'Alex',
  last_name: 'Morgan',
  companyName: 'ProStep Athletics',
  website: 'https://example.com/prostep-demo',
  profileSummary: 'Demo partner profile for the ARGUS campaign experience.',
  campaigns: [
    {
      id: 1,
      name: 'Summer Skills Challenge',
      type: 'challenge',
      sport: 'Basketball',
      ageBands: ['U13–U15', 'U17–U21'],
      impressions: 12400,
      joins: 3080,
      completions: 1204,
      engagement: 8.3,
      status: 'active',
    },
    {
      id: 2,
      name: 'Copenhagen Million Shot Goal',
      type: 'community_goal',
      sport: 'Basketball',
      ageBands: ['All ages'],
      impressions: 8200,
      joins: 1840,
      completions: 0,
      engagement: 11.4,
      progress: 782450,
      target: 1000000,
      unit: 'made shots',
      status: 'active',
    },
    {
      id: 3,
      name: 'Back to School Drive',
      type: 'quest',
      sport: 'All sports',
      ageBands: ['U9–U11', 'U13–U15'],
      impressions: 0,
      joins: 0,
      completions: 0,
      engagement: 0,
      status: 'scheduled',
    },
  ],
  overview: {
    totalReach: 20600,
    avgEngagement: 9.9,
    activeCampaigns: 2,
    totalCompletions: 1204,
    badgesIssued: 1204,
    communityGoals: 1,
  },
}

export const mockUsersByRole = {
  player: mockPlayer,
  coach: mockCoach,
  club: mockClub,
  sponsor: mockSponsor,
}
